/** @jsxRuntime automatic @jsxImportSource react */
// Ausdruecklich: Die API laedt diese Datei im Entwicklungsbetrieb und im
// e2e-Lauf ueber tsx, und tsx wendet die tsconfig nur auf Dateien des eigenen
// Projekts an — fuer ein Paket daneben fiele es auf das klassische JSX
// (`React.createElement` ohne Import) zurueck: „React is not defined".
import type {
  StatusReportResponse,
  StatusReportTicket,
  StatusReportTicketStatsRow,
} from "@msc/shared";

import { safeHttpUrl } from "./url.js";
import {
  type BudgetBarKind,
  budgetBarSegments,
  budgetPercentText,
  budgetUsedPct,
} from "./budget.js";
import { formatEur, formatHours, formatMonth } from "./format.js";
import { berichtT } from "./i18n.js";
import { REPORT_LOCALE } from "./reportLanguage.js";
import { ticketRowMetrics } from "./ticketListLayout.js";

/**
 * Originalgetreue Darstellung des Service-Statusberichts.
 *
 * Aufbau, Farben und Maße sind 1:1 aus `report_html.py` der abgelösten App
 * `ServiceStatusberichte` übernommen — bis hin zu den Tabellen-Layouts. Das ist
 * kein Selbstzweck: Der Bericht wird per „Bericht kopieren" als `text/html` in
 * die Zwischenablage gelegt und in Outlook eingefügt. Outlook rendert kein
 * Flexbox/Grid und wirft beim Einfügen helle Schrift auf farbigem Grund auf
 * Schwarz — daher Tabellen, `bgcolor` und ausschließlich dunkle Schrift auf
 * hellem Grund mit farbigen Rändern/Balken („pastesafe" im Original).
 *
 * Deshalb hier bewusst KEINE Mantine-Komponenten und keine CSS-Klassen: Was
 * kopiert wird, muss ohne das Stylesheet der App funktionieren.
 */

const BRAND = {
  orange: "#EA5B0B",
  gold: "#FDC300",
  grey: "#575756",
  green: "#688E26",
  purple: "#9B59B6",
  blue: "#3498DB",
  red: "#E74C3C",
  /** Der Teil des Balkens jenseits des Vertragsvolumens (Befund B2). */
  redDark: "#A93226",
  line: "#EDEDED",
  border: "#E2E2E2",
  /**
   * Eine Grau-Treppe statt beliebiger Einzelwerte.
   *
   * Vorher standen im Bericht dreizehn verschiedene Graustufen — `#333`,
   * `#444`, `#555`, `#666`, `#888`, `#999`, `#AAA`, `#CCC` und mehr, meist
   * an Ort und Stelle entschieden. Für das Auge ist das kein Unterschied,
   * sondern Unruhe: Gleich Wichtiges sah verschieden aus. Vier Stufen
   * reichen, und sie tragen ihre Aufgabe im Namen.
   */
  /** Sehr helle Fläche für Kästen und Zebrastreifen. */
  wash: "#FAFAF8",
  /** Getönte Markenfläche — Badges, Initialen, Datumskachel. */
  tint: "#FDF0E7",
  /** Kante zur getönten Fläche. */
  tintLine: "#F3D9C4",
  ink: "#2E2E2D",
  body: "#4A4A48",
  muted: "#8C8C88",
  faint: "#B3B3AE",
  hairline: "#E8E8E4",
  boxBg: "#F6F6F4",
  headBg: "#F2F2F2",
  barRest: "#E6E6E2",
} as const;

const FONT = "Arial,sans-serif";

/**
 * VERSALIEN AUSSCHREIBEN, nicht über CSS erzeugen.
 *
 * `text-transform` überlebt den Einfüge-Filter nicht — gemessen am 2026-09-04
 * an zwei echten, an sich selbst geschickten Mails: **null** Vorkommen von
 * `text-transform` in beiden Fassungen, weder über Outlook Desktop noch über
 * Outlook im Web. Aus „BUDGETÜBERSICHT" wurde beim Empfänger wieder
 * „Budgetübersicht", und damit war die ganze Auszeichnung der Überschriften,
 * Spaltenköpfe und Kennzahlen-Marken weg.
 *
 * Was im Text steht, kann kein Filter kleinschreiben. Die CSS-Angabe bleibt
 * trotzdem stehen: Sie kostet nichts und hält die Ansicht im Browser gleich,
 * falls jemand die Zeichenkette später doch klein übergibt.
 */
function versal(text: string): string {
  return text.toLocaleUpperCase("de-DE");
}

/**
 * `bgcolor` als Attribut statt als CSS.
 *
 * Outlook ignoriert `background-color` auf Tabellenzellen zuverlässig, das
 * Alt-Attribut `bgcolor` nicht — deshalb steht es im Original an jeder farbigen
 * Zelle. React kennt es in seinen DOM-Typen nicht, gibt es aber unverändert
 * aus; der Umweg über ein `Record` hält den Typecheck sauber, ohne `any`.
 * Browser rendern `bgcolor` ebenfalls, die Ansicht in der App stimmt also mit
 * dem überein, was kopiert wird.
 */
function bg(color: string): Record<string, string> {
  return { bgcolor: color };
}

/**
 * Monatskürzel für die Datums-Kachel der Events — wie `_MO_SHORT` im Original.
 * Bewusst eine eigene Liste statt `Intl`: Das Original schreibt „MRZ", `Intl`
 * liefert für de-DE „Mär.".
 */
const MONTHS_SHORT = [
  "JAN", "FEB", "MRZ", "APR", "MAI", "JUN",
  "JUL", "AUG", "SEP", "OKT", "NOV", "DEZ",
];

/**
 * „März 2026" — in der Sprache des Berichts, nicht der Oberfläche. Die
 * frühere feste Liste `MONTHS_DE` neben `t()`-Aufrufen erzeugte Zeilen wie
 * „Completed tickets – März 2026" (Befund W8).
 */
export function monthLabel(reportMonth: string): string {
  return formatMonth(reportMonth, REPORT_LOCALE);
}

/** Beträge im Bericht — immer im Zahlenformat des Berichts. */
function fmtEur(v: number | null | undefined): string {
  return formatEur(v, REPORT_LOCALE);
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/** Statusfarbe wie `report_html.py::stat_col`. */
function statusColor(status: string | null): string {
  const s = (status ?? "").toLowerCase();
  if (s.includes("done") || s.includes("erledigt")) return BRAND.green;
  if (s.includes("testing") || s.includes("to test")) return BRAND.purple;
  if (s.includes("warte") || s.includes("waiting")) return BRAND.blue;
  return BRAND.orange;
}

/**
 * Helle Tönung je Statusfarbe — der Grund der Status-Badges.
 *
 * Gegenstück zu `STAT_TINT` im Original. Getönter Grund plus farbiger fetter
 * Text statt eines Rahmens: Die Word-Engine kennt kein `border-radius`, malt
 * den Rahmen aber sehr wohl — aus der schlanken Pille wurde beim Einfügen ein
 * eckiger Klotz. Ohne Rahmen gibt es nichts, was eckig werden könnte.
 */
const STAT_TINT: Record<string, string> = {
  [BRAND.green]: "#EFF5E6",
  [BRAND.purple]: "#F5EEF8",
  [BRAND.blue]: "#EAF4FB",
  [BRAND.orange]: "#FDF0E7",
};

function statusTint(color: string): string {
  return STAT_TINT[color] ?? BRAND.headBg;
}

/**
 * Marke und Wert einer Kennzahl — kleine Versalien über einer festen Zahl.
 *
 * Als Konstante, nicht als Wiederholung an drei Stellen: Sobald die drei
 * Spalten auseinanderlaufen, liest sich die Reihe nicht mehr als Reihe.
 */
const KPI_LABEL = {
  fontFamily: FONT,
  fontSize: 9,
  fontWeight: "bold" as const,
  color: BRAND.faint,
  textTransform: "uppercase" as const,
  letterSpacing: ".9px",
  lineHeight: "16px",
};

const KPI_VALUE = {
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: "bold" as const,
  color: BRAND.body,
  lineHeight: "26px",
};

/** Ampel für den Budget-Verbrauch — < 70 % grün, < 90 % orange, sonst rot. */
function budgetColor(usedPct: number): string {
  if (usedPct < 70) return BRAND.green;
  if (usedPct < 90) return BRAND.orange;
  return BRAND.red;
}

/**
 * Abschnitts-Überschrift: kurze Goldkante, dunkler Titel, Haarlinie darunter.
 *
 * Vorher trug jeder Abschnitt seinen Titel in Orange — zusammen mit Eyebrow,
 * Prozentzahl, Restbetrag, Ticketfarben und Datumskachel waren das sechs
 * Orangetöne, die um dieselbe Aufmerksamkeit stritten. Eine Signalfarbe
 * signalisiert aber nur, solange sie selten ist. Die Titel stehen deshalb
 * jetzt in Dunkelgrau, und Orange bleibt den Werten vorbehalten, auf die es
 * ankommt.
 *
 * Die Goldkante ersetzt das frühere Gold-Quadrat: dieselbe Markenfarbe an
 * derselben Stelle, aber als ruhige Kante statt als Kästchen. Sie ist eine
 * Farbfläche, kein Rahmen — die Word-Engine zeichnet sie deshalb mit.
 */
function Section({ title }: { title: string }) {
  return (
    <table width="100%" cellPadding={0} cellSpacing={0} border={0}>
      <tbody>
        <tr>
          <td height={34} style={{ fontSize: 1, lineHeight: "1px" }}>
            &nbsp;
          </td>
        </tr>
        <tr>
          {/*
            Die Goldkante sitzt als RAHMEN am Titel, nicht als eigene Zelle
            darueber.

            Vorher war sie ein 22x3-Feld in einer eigenen Zeile. Im Browser
            klebte sie am Titel und las sich als Marke; Outlook setzt zwischen
            Tabellenzeilen aber eigenen Abstand, und daraus wurden freistehende
            gelbe Striche ueber jeder Ueberschrift — in der Mail der auffaelligste
            und haesslichste Teil der Seite (Rueckmeldung 04.09.).

            Als linker Rahmen kann das nicht mehr passieren: Ein Rahmen gehoert
            zur Zelle und wandert nicht von ihr weg. Die Word-Engine zeichnet
            Rahmen zuverlaessig — anders als Rundungen wirft sie sie nicht weg.
          */}
          <td
            style={{
              borderLeft: `3px solid ${BRAND.gold}`,
              borderBottom: `1px solid ${BRAND.hairline}`,
              paddingLeft: 12,
              paddingTop: 2,
              paddingBottom: 10,
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: "bold",
              letterSpacing: "1.4px",
              textTransform: "uppercase",
              color: BRAND.ink,
            }}
          >
            {versal(title)}
          </td>
        </tr>
        <tr>
          <td height={10} style={{ fontSize: 1, lineHeight: "1px" }}>
            &nbsp;
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * Ticketnummer, verlinkt aufs Kundenportal — wie `report_html.py::key_link`.
 * Ohne konfigurierte Basis-URL bleibt es reiner Text statt eines toten Links.
 */
function TicketKey({
  jiraKey,
  baseUrl,
  color = BRAND.grey,
}: {
  jiraKey: string | null;
  baseUrl: string | null;
  color?: string;
}) {
  if (!jiraKey) return null;
  const style = {
    fontFamily: FONT,
    fontSize: 11,
    fontWeight: "bold" as const,
    color,
    textDecoration: "none",
    whiteSpace: "nowrap" as const,
  };
  if (!baseUrl) return <span style={style}>{jiraKey}</span>;
  return (
    <a
      href={`${baseUrl.replace(/\/+$/, "")}/${jiraKey}`}
      target="_blank"
      rel="noopener noreferrer"
      style={style}
    >
      {jiraKey}
    </a>
  );
}

/**
 * Status-Badge: getönter Grund, farbiger fetter Text, kein Rahmen.
 *
 * `borderRadius` ist bewusst ein Enhancement — neues Outlook und OWA runden,
 * die Word-Engine ignoriert es folgenlos. Tragend sind `nowrap` und eine
 * ausreichend breite Spalte (130 px, s. `TicketHeaderRow`): Ohne beides bricht
 * „Warte auf Rückmeldung" um und die Zeile wird doppelt hoch.
 *
 * Der Grund steht als `bgcolor`-Attribut **und** als CSS — das Attribut
 * überlebt den Word-Filter, die CSS-Angabe die Web-Engine des neuen Outlook.
 */
function StatusPill({ status }: { status: string | null }) {
  const col = statusColor(status);
  const tint = statusTint(col);
  return (
    <td
      {...bg(tint)}
      style={{
        backgroundColor: tint,
        borderRadius: 10,
        padding: "3px 10px",
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: "bold",
        whiteSpace: "nowrap",
        textAlign: "center",
        color: col,
      }}
    >
      {status ?? ""}
    </td>
  );
}

function TicketHeaderRow({
  ticketLabel,
  summaryLabel,
  lastLabel,
  nextStepsLabel,
  accent,
}: {
  ticketLabel: string;
  summaryLabel: string;
  lastLabel: string;
  /**
   * Beschriftung der Spalte „Status und nächste Schritte" — oder `undefined`,
   * wenn sie in diesem Abschnitt gar nicht erscheint (s. `hatNaechsteSchritte`).
   */
  nextStepsLabel?: string | undefined;
  accent: string;
}) {
  const th = {
    fontFamily: FONT,
    fontSize: 10,
    fontWeight: "bold" as const,
    color: BRAND.body,
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
  };
  // Der Kopf trägt eine Haarlinie statt eines grauen Bandes: Er soll die
  // Spalten benennen, nicht mit ihnen um Aufmerksamkeit ringen.
  const cell = {
    padding: "7px 12px",
    borderBottom: `1px solid ${BRAND.hairline}`,
  };
  return (
    <tr {...bg("#FFFFFF")}>
      <td {...bg(accent)} width={3} style={{ fontSize: 1 }}>
        &nbsp;
      </td>
      {/* 92 statt 78: „MS-2969" brach in der echten Mail auf zwei Zeilen um.
          Die Nummern des Bestands sind laenger als die des Testdatensatzes. */}
      <td style={cell} width={92}>
        <span style={th}>{versal(ticketLabel)}</span>
      </td>
      <td style={cell}>
        <span style={th}>{versal(summaryLabel)}</span>
      </td>
      {/* 130 px: Unter ~130 bricht „Warte auf Rückmeldung" trotz `nowrap` um,
          weil die Spalte dann schmaler ist als das Badge. */}
      <td style={cell} width={130}>
        <span style={th}>{versal(lastLabel)}</span>
      </td>
      {nextStepsLabel === undefined ? null : (
        <td style={cell} width={230}>
          <span style={th}>{versal(nextStepsLabel)}</span>
        </td>
      )}
    </tr>
  );
}

/**
 * Trägt in diesem Abschnitt überhaupt jemand einen nächsten Schritt ein?
 *
 * Die Spalte ist **optional im Wortsinn**: Ist bei keinem Vorgang etwas
 * gepflegt, erscheint sie gar nicht. Eine leere Spalte beim Kunden wäre
 * schlechter als keine — sie sähe aus wie eine vergessene Pflicht.
 *
 * Je Abschnitt entschieden, nicht für den ganzen Bericht: Wer bei den offenen
 * Vorgängen etwas notiert, will deswegen keine leere Spalte bei den
 * erledigten.
 */
function hatNaechsteSchritte(tickets: StatusReportTicket[]): boolean {
  return tickets.some((t) => (t.nextSteps ?? "").trim() !== "");
}

/**
 * Die Zelle mit dem Freitext.
 *
 * Bewusst ohne `nowrap` und mit normaler Zeilenhöhe: Hier stehen ganze Sätze
 * („Interne Rücksprache mit ISM ausstehend"), keine Schlagworte. Sie dürfen
 * und sollen umbrechen.
 */
function NextStepsCell({
  text,
  padding,
}: {
  text: string | null;
  padding: string;
}) {
  return (
    <td
      width={230}
      style={{
        width: 230,
        padding,
        borderBottom: `1px solid ${BRAND.hairline}`,
        fontFamily: FONT,
        fontSize: 12,
        color: BRAND.body,
        lineHeight: 1.5,
        verticalAlign: "top",
      }}
    >
      {text ?? ""}
    </td>
  );
}

/**
 * Ticketstatistik: 24 Monate nach Vorgangsart.
 *
 * Spalten, Reihenfolge und Beschriftungen stammen aus der abgeloesten
 * Anwendung — der Kunde bekommt diese Tabelle seit Jahren, und sie soll
 * wiedererkennbar bleiben. Die Farben sind die des heutigen Berichts, damit
 * sie nicht wie ein Fremdkoerper zwischen den uebrigen Abschnitten steht.
 *
 * **Zebra ueber `bgcolor` UND CSS**, wie ueberall hier: Der Paste-Filter wirft
 * das eine oder das andere weg, je nach Weg in die Mail. Die Textfarben
 * bekommen ihr `<font color>` nicht hier, sondern auf dem Kopierweg
 * (`outlookHtml.ts`) — im JSX stehen sie als CSS.
 */
function TicketStatsTable({ rows }: { rows: StatusReportTicketStatsRow[] }) {
  // Fest in der Berichtssprache wie der Rest des Berichts. Vorher folgte
  // diese Tabelle als einzige der Oberflaechensprache — bei englischer
  // Oberflaeche standen englische Spaltenkoepfe in einem deutschen Bericht.
  const t = berichtT;

  const kopf = {
    fontFamily: FONT,
    fontSize: 10,
    fontWeight: "bold" as const,
    color: BRAND.muted,
    letterSpacing: "0.5px",
  };

  const spalten: Array<[string, keyof StatusReportTicketStatsRow]> = [
    ["colIncidents", "incident"],
    ["colProblems", "problem"],
    ["colChanges", "change"],
    ["colServiceRequests", "serviceRequest"],
    ["colOpen", "open"],
  ];

  return (
    <table
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      style={{
        marginBottom: 18,
        border: `1px solid ${BRAND.border}`,
        borderCollapse: "collapse",
      }}
    >
      <tbody>
        <tr {...bg(BRAND.wash)} style={{ backgroundColor: BRAND.wash }}>
          <td style={{ padding: "6px 10px" }}>
            <span style={kopf}>
              {versal(t("pages.statusReports.report.colMonth"))}
            </span>
          </td>
          {spalten.map(([schluessel]) => (
            <td key={schluessel} align="center" style={{ padding: "6px 8px" }}>
              <span style={{ ...kopf, whiteSpace: "nowrap" }}>
                {versal(t(`pages.statusReports.report.${schluessel}`))}
              </span>
            </td>
          ))}
        </tr>
        {rows.map((zeile, i) => {
          // Gerade Zeilen weiss, ungerade getoent — wie in der Altanwendung.
          const flaeche = i % 2 === 0 ? "#FFFFFF" : BRAND.wash;
          return (
            <tr
              key={zeile.month}
              {...bg(flaeche)}
              style={{ backgroundColor: flaeche }}
            >
              <td
                style={{
                  padding: "5px 10px",
                  borderBottom: `1px solid ${BRAND.hairline}`,
                  fontFamily: FONT,
                  fontSize: 12,
                  fontWeight: "bold",
                  color: BRAND.grey,
                  whiteSpace: "nowrap",
                }}
              >
                {zeile.label}
              </td>
              {spalten.map(([schluessel, feld]) => (
                <td
                  key={schluessel}
                  align="center"
                  style={{
                    padding: "6px 8px",
                    borderBottom: `1px solid ${BRAND.hairline}`,
                    fontFamily: FONT,
                    fontSize: 12,
                    color: BRAND.body,
                  }}
                >
                  {String(zeile[feld])}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ReportView({ report }: { report: StatusReportResponse }) {
  /**
   * Fester Sprachkanal für den kundengerichteten Teil (Befund W8, Fachkonzept
   * §13.1). Die Texte stehen in den Sprachdateien dieses Pakets (`i18n.ts`) —
   * ausgewählt wird nicht nach der Oberflächensprache, sondern nach der Sprache
   * des Berichts. Kein Hook: Server und Browser rendern ohne App-Instanz.
   */
  const t = berichtT;
  const month = monthLabel(report.reportMonth);
  const lead = report.contacts.find((c) => c.role === "lead");
  const signers =
    report.contacts
      .slice(0, 2)
      .map((c) => c.fullName)
      .join(" & ") || (lead?.fullName ?? "");

  // Erledigte Tickets teilen sich wie im Original in Gold-Kasten und Restliste.
  /*
   * Ausgeblendete Vorgaenge fallen HIER weg, nicht schon im Service.
   *
   * Der Grund ist die Bearbeitungsansicht: Sie zeigt dieselben Daten und muss
   * die Zeile weiter sehen, sonst gaebe es keinen Weg, sie wieder
   * einzublenden. Der Kundenbericht ist die Darstellung, und nur dort ist die
   * Entscheidung wirksam.
   */
  const openTickets = report.openTickets.filter((ticket) => !ticket.hidden);
  const doneTickets = report.doneTickets.filter((ticket) => !ticket.hidden);

  const highlightItems = doneTickets.filter((ticket) => ticket.highlight);
  const regularDone = doneTickets.filter((ticket) => !ticket.highlight);

  // A4: alle zeigen — aber ab einer gewissen Laenge enger, mit Kopfzeile und
  // Datumsanker. Warum und ab wann: `ticketListLayout.ts`.
  /*
   * Die Spalte „Status und naechste Schritte" gibt es nur bei den OFFENEN
   * Vorgaengen, und dort nur, wenn auch etwas gepflegt ist.
   *
   * Nicht bei den erledigten: Was abgeschlossen ist, hat keinen naechsten
   * Schritt mehr. Eine Spalte dafuer waere eine Einladung, sie mit
   * Rueckblicken zu fuellen — dafuer gibt es den Nutzentext am Highlight.
   */
  const offeneMitSchritten = hatNaechsteSchritte(openTickets);

  const doneRow = ticketRowMetrics(regularDone.length);
  const openRow = ticketRowMetrics(openTickets.length);
  const doneCompact = doneRow.compact;

  const totalBudget = report.budgets.reduce((s, b) => s + (b.budgetEur ?? 0), 0);
  const totalBilled = report.budgets.reduce((s, b) => s + (b.billedEur ?? 0), 0);
  const totalLeft = report.budgets.reduce((s, b) => s + (b.leftEur ?? 0), 0);

  return (
    /*
      Der aeusserste Rahmen traegt seinen Hintergrund als ATTRIBUT, nicht nur
      als CSS — und ist deshalb eine Tabelle, kein `div`.

      **Der Anlass (Rueckmeldung Markus Wolle, 06.10.2026).** Eingefuegt in ein
      Outlook im Dunkelmodus kam der Bericht schwarz heraus: heller Text auf
      dunklem Grund, waehrend die Status-Badges ihre hellen Flaechen behielten.
      Genau dieses halb umgefaerbte Bild sagt, woran es liegt. Die Badges
      tragen `bgcolor` und ueberlebten; der Rest hing am CSS-Hintergrund dieses
      einen Elements, und der fiel weg. Uebrig blieb der dunkle Grund des
      Verfassen-Fensters.

      `bgcolor` gibt es nur an Tabellenelementen, an einem `div` ist es
      wirkungslos. Die Hausregel „jede Farbe doppelt, als Attribut UND als
      CSS" (s. Gotchas) war hier also gar nicht erfuellbar, solange der
      Rahmen ein `div` war.

      KEIN `align` auf dieser Tabelle — das waere ein Float, s. unten.
    */
    <table
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      {...bg(BRAND.wash)}
      style={{ backgroundColor: BRAND.wash }}
    >
      <tbody>
        <tr>
          <td
            {...bg(BRAND.wash)}
            style={{ backgroundColor: BRAND.wash, padding: "14px 10px" }}
          >
      {/*
        KEIN `align` auf dieser aeusseren Tabelle.

        `align="left"` sieht wie eine Ausrichtung aus, ist aber ein **Float**:
        Alles, was in der Mail nach dem Bericht kommt, fliesst dann rechts
        daneben. In einem echten Entwurf stand die Grussformel deshalb auf
        halber Hoehe neben dem Bericht statt darunter (07.09.2026). Ohne
        `align` ist eine Tabelle ein Blockelement und steht ohnehin links —
        die Ausrichtung aendert sich also nicht, nur das Umfliessen faellt weg.
        Festgehalten in `ReportView.outlook.test.tsx`.
      */}
      <table width={760} cellPadding={0} cellSpacing={0} border={0}>
        <tbody>
          <tr>
            <td>
              {/*
                Kopf als Titelseite, nicht als Kasten.

                Vorher saß der Kundenname in einem grauen Kasten hinter einer
                senkrechten Leiste — ein Element aus der Oberfläche, das in
                einer Mail wie ein Zitatblock wirkt. Jetzt trägt eine kräftige
                Orangekante oben den Bericht an, darunter steht der Name frei
                auf Weiß. Das liest sich als Dokument statt als Widget und gibt
                dem wichtigsten Wort der Seite den Platz, den es braucht.
              */}
              <table
                width={760}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                {...bg("#FFFFFF")}
                style={{
                  borderLeft: `1px solid ${BRAND.border}`,
                  borderRight: `1px solid ${BRAND.border}`,
                }}
              >
                <tbody>
                  <tr>
                    <td
                      {...bg(BRAND.orange)}
                      height={4}
                      style={{ fontSize: 1, lineHeight: "1px" }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                  <tr>
                    <td
                      {...bg(BRAND.wash)}
                      style={{
                        backgroundColor: BRAND.wash,
                        borderBottom: `1px solid ${BRAND.hairline}`,
                        padding: "26px 26px 22px",
                      }}
                    >
                      {/*
                        Der Berichtsmonat steht rechts als Marke, nicht mehr
                        im Fliesstext darunter.

                        Er ist die zweitwichtigste Angabe des Kopfes — welcher
                        Zeitraum. Vorher stand er als erstes Wort einer grauen
                        Zeile zwischen Kundenname und Service Lead und ging
                        dort unter, waehrend die rechte Haelfte des Kopfes
                        leer blieb. Jetzt traegt sie ihn.
                      */}
                      <table width="100%" cellPadding={0} cellSpacing={0} border={0}>
                        <tbody>
                          <tr>
                            <td valign="middle">
                              <span
                                style={{
                                  fontFamily: FONT,
                                  fontSize: 10,
                                  fontWeight: "bold",
                                  color: BRAND.orange,
                                  textTransform: "uppercase",
                                  letterSpacing: "2.5px",
                                }}
                              >
                                {versal(t("pages.statusReports.report.eyebrow"))}
                              </span>
                            </td>
                            <td align="right" valign="middle" width={150}>
                              <table cellPadding={0} cellSpacing={0} border={0} align="right">
                                <tbody>
                                  <tr>
                                    <td
                                      {...bg(BRAND.tint)}
                                      align="center"
                                      valign="middle"
                                      height={22}
                                      style={{
                                        backgroundColor: BRAND.tint,
                                        padding: "0 12px",
                                        fontFamily: FONT,
                                        fontSize: 11,
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        lineHeight: "22px",
                                        color: BRAND.orange,
                                      }}
                                    >
                                      {month}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <p
                        style={{
                          margin: "10px 0 8px",
                          fontFamily: FONT,
                          fontSize: 30,
                          fontWeight: "bold",
                          color: BRAND.ink,
                          lineHeight: "34px",
                        }}
                      >
                        {report.customerName}
                      </p>
                      {lead ? (
                        <p
                          style={{
                            margin: 0,
                            fontFamily: FONT,
                            fontSize: 12,
                            color: BRAND.muted,
                          }}
                        >
                          {t("pages.statusReports.report.serviceLead")}:{" "}
                          <strong style={{ color: BRAND.body }}>
                            {lead.fullName}
                          </strong>
                        </p>
                      ) : null}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={760}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                {...bg("#FFFFFF")}
                style={{
                  borderLeft: `1px solid ${BRAND.border}`,
                  borderRight: `1px solid ${BRAND.border}`,
                }}
              >
                <tbody>
                  <tr>
                    <td style={{ padding: "0 26px 22px 26px" }}>
                      {/* ── Budgetübersicht ─────────────────────────────── */}
                      {report.budgets.length > 0 && (
                        <>
                          <Section
                            title={t("pages.statusReports.report.budgetSection")}
                          />
                          <table
                            width="100%"
                            cellPadding={0}
                            cellSpacing={0}
                            border={0}
                            style={{ marginBottom: 18 }}
                          >
                            <tbody>
                              <tr>
                                <td
                                  {...bg("#FFFFFF")}
                                  style={{
                                    backgroundColor: "#FFFFFF",
                                    border: `1px solid ${BRAND.hairline}`,
                                    borderRadius: 8,
                                    padding: "20px 22px 18px",
                                  }}
                                >
                                  {report.budgets.map((b) => {
                                    const usedPct = budgetUsedPct(b);
                                    const col = budgetColor(usedPct);
                                    // Die Balkenbreiten stecken in `budget.ts`
                                    // — die Zahl daneben nennt weiterhin den
                                    // echten, ungekappten Wert (W6).
                                    const segments = budgetBarSegments(usedPct);
                                    const segmentColor: Record<
                                      BudgetBarKind,
                                      string
                                    > = {
                                      used: col,
                                      remaining: BRAND.barRest,
                                      overrun: BRAND.redDark,
                                    };
                                    return (
                                      <table
                                        key={b.projectId}
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        style={{ marginBottom: 4 }}
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                fontFamily: FONT,
                                                fontSize: 14,
                                                fontWeight: "bold",
                                                color: BRAND.ink,
                                                paddingBottom: 8,
                                              }}
                                            >
                                              {b.name}
                                              {/* Bestellnummer, falls
                                                  gepflegt: Sie steht direkt
                                                  unter dem Budgetnamen, damit
                                                  der Kunde die Rechnung
                                                  zuordnen kann. Ohne
                                                  gepflegte Nummer steht hier
                                                  nichts — optional heisst
                                                  nicht vorhanden. */}
                                              {b.orderNumber && (
                                                <span
                                                  style={{
                                                    display: "block",
                                                    fontFamily: FONT,
                                                    fontSize: 11,
                                                    fontWeight: "normal",
                                                    color: BRAND.muted,
                                                    paddingTop: 2,
                                                  }}
                                                >
                                                  {t(
                                                    "pages.statusReports.report.orderNumber",
                                                  )}
                                                  {": "}
                                                  {b.orderNumber}
                                                </span>
                                              )}
                                            </td>
                                            <td
                                              align="right"
                                              valign="middle"
                                              width={130}
                                              style={{ width: 130, paddingBottom: 8 }}
                                            >
                                              {/* Als Marke auf getoentem Grund,
                                                  in derselben Sprache wie die
                                                  Status-Badges — der Wert ist
                                                  eine Einstufung, kein Satz. */}
                                              <table cellPadding={0} cellSpacing={0} border={0} align="right">
                                                <tbody>
                                                  <tr>
                                                    <td
                                                      {...bg(statusTint(col))}
                                                      align="center"
                                                      valign="middle"
                                                      height={20}
                                                      style={{
                                                        backgroundColor: statusTint(col),
                                                        padding: "0 10px",
                                                        fontFamily: FONT,
                                                        fontSize: 11,
                                                        fontWeight: "bold",
                                                        whiteSpace: "nowrap",
                                                        textAlign: "center",
                                                        lineHeight: "20px",
                                                        color: col,
                                                      }}
                                                    >
                                                      {budgetPercentText(b)}
                                                      {" "}
                                                      {t(
                                                        "pages.statusReports.report.usedSuffix",
                                                      )}
                                                    </td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td colSpan={2} style={{ paddingBottom: 14 }}>
                                              {/* Farbig = VERBRAUCHT (grün wenig,
                                                  rot viel), grau = verbleibend,
                                                  dunkelrot = jenseits des
                                                  Volumens (Befund B2). */}
                                              <table
                                                width="100%"
                                                cellPadding={0}
                                                cellSpacing={0}
                                                border={0}
                                              >
                                                <tbody>
                                                  <tr>
                                                    {segments.map((seg) => (
                                                      <td
                                                        key={seg.kind}
                                                        {...bg(
                                                          segmentColor[seg.kind],
                                                        )}
                                                        data-bar-segment={seg.kind}
                                                        width={`${seg.widthPct}%`}
                                                        height={10}
                                                        style={{
                                                          fontSize: 1,
                                                          lineHeight: "1px",
                                                        }}
                                                      >
                                                        &nbsp;
                                                      </td>
                                                    ))}
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </td>
                                          </tr>
                                          {/*
                                            Die drei Zahlen als Reihe statt als
                                            Satz.

                                            Vorher stand der Restbetrag links
                                            groß, daneben rechts eine Zeile
                                            Fließtext: „Budget 12.600 € ·
                                            Verbraucht 8.400 €". Wer die Werte
                                            vergleichen wollte, musste lesen
                                            statt zu schauen. Drei Spalten
                                            gleicher Bauart — Marke oben, Wert
                                            darunter — machen aus dem Satz eine
                                            Tabelle. Der Rest behält als
                                            einziger die Ampelfarbe und die
                                            große Type: Er ist die Zahl, wegen
                                            der der Kunde den Bericht öffnet.
                                          */}
                                          <tr>
                                            <td colSpan={2}>
                                              <table
                                                width="100%"
                                                cellPadding={0}
                                                cellSpacing={0}
                                                border={0}
                                              >
                                                <tbody>
                                                  <tr>
                                                    <td valign="top" width={260} style={{ width: 260 }}>
                                                      <span style={KPI_LABEL}>
                                                        {versal(
                                                          t(
                                                            "pages.statusReports.report.remaining",
                                                          ),
                                                        )}
                                                      </span>
                                                      <br />
                                                      <span
                                                        style={{
                                                          fontFamily: FONT,
                                                          fontSize: 26,
                                                          fontWeight: "bold",
                                                          color: col,
                                                          lineHeight: "30px",
                                                        }}
                                                      >
                                                        {fmtEur(b.leftEur)}
                                                      </span>
                                                    </td>
                                                    {/* Senkrechte Haarlinien als
                                                        RAHMEN der Zelle, nicht als
                                                        eigene 1px-Spalte: Eine so
                                                        schmale Zelle bekommt in Word
                                                        keine verlaessliche Breite,
                                                        ein Rahmen schon. */}
                                                    <td
                                                      valign="top"
                                                      width={165}
                                                      style={{
                                                        width: 165,
                                                        borderLeft: `1px solid ${BRAND.hairline}`,
                                                        paddingLeft: 18,
                                                      }}
                                                    >
                                                      <span style={KPI_LABEL}>
                                                        {versal(
                                                          t(
                                                            "pages.statusReports.report.budgetWord",
                                                          ),
                                                        )}
                                                      </span>
                                                      <br />
                                                      <span style={KPI_VALUE}>
                                                        {fmtEur(b.budgetEur)}
                                                      </span>
                                                    </td>
                                                    <td
                                                      valign="top"
                                                      width={165}
                                                      style={{
                                                        width: 165,
                                                        borderLeft: `1px solid ${BRAND.hairline}`,
                                                        paddingLeft: 18,
                                                      }}
                                                    >
                                                      <span style={KPI_LABEL}>
                                                        {versal(
                                                          t(
                                                            "pages.statusReports.report.consumed",
                                                          ),
                                                        )}
                                                      </span>
                                                      <br />
                                                      <span style={KPI_VALUE}>
                                                        {fmtEur(b.billedEur)}
                                                      </span>
                                                    </td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </td>
                                          </tr>
                                          {/*
                                            Abstand als eigene Zeile, nicht als
                                            `margin` an der Tabelle.

                                            Bei zwei Servicetöpfen klebte der
                                            zweite Name unmittelbar unter den
                                            Zahlen des ersten, und die Blöcke
                                            lasen sich als eine einzige lange
                                            Liste. `margin` an einer Tabelle
                                            setzt die Word-Engine unzuverlässig
                                            um; eine Zeile mit Höhe kommt
                                            überall an.
                                          */}
                                          <tr>
                                            <td
                                              colSpan={2}
                                              height={24}
                                              style={{ fontSize: 1, lineHeight: "1px" }}
                                            >
                                              &nbsp;
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    );
                                  })}

                                  {report.budgets.length > 1 && (
                                    <table
                                      width="100%"
                                      cellPadding={0}
                                      cellSpacing={0}
                                      border={0}
                                    >
                                      <tbody>
                                        <tr>
                                          {/*
                                            Die Summe in DERSELBEN Form wie die
                                            Einzelbudgets darüber.

                                            Vorher stand hier links der
                                            Gesamtrest und rechts ein Satz
                                            Fließtext („Gesamt-Budget 24.800 €,
                                            Verbraucht 20.576 €"). Wer zwei
                                            Servicetöpfe hat — und das ist im
                                            Bestand der Regelfall — sah oben
                                            zweimal eine saubere Dreierreihe und
                                            darunter plötzlich einen Satz.
                                            Dieselben drei Zahlen gehören in
                                            dieselbe Form, sonst liest sich die
                                            Summe wie eine Fußnote statt wie das
                                            Ergebnis.
                                          */}
                                          <td
                                            valign="top"
                                            width={260}
                                            {...bg(BRAND.wash)}
                                            style={{
                                              width: 260,
                                              backgroundColor: BRAND.wash,
                                              borderTop: `2px solid ${BRAND.border}`,
                                              padding: "12px 0 12px 14px",
                                            }}
                                          >
                                            <span style={KPI_LABEL}>
                                              {versal(
                                                t(
                                                  "pages.statusReports.report.totalRemaining",
                                                ),
                                              )}
                                            </span>
                                            <br />
                                            <span
                                              style={{
                                                fontFamily: FONT,
                                                fontSize: 26,
                                                fontWeight: "bold",
                                                lineHeight: "30px",
                                                color: budgetColor(
                                                  totalBudget
                                                    ? (totalBilled / totalBudget) * 100
                                                    : 0,
                                                ),
                                              }}
                                            >
                                              {fmtEur(totalLeft)}
                                            </span>
                                          </td>
                                          <td
                                            valign="top"
                                            width={165}
                                            {...bg(BRAND.wash)}
                                            style={{
                                              width: 165,
                                              backgroundColor: BRAND.wash,
                                              borderTop: `2px solid ${BRAND.border}`,
                                              borderLeft: `1px solid ${BRAND.border}`,
                                              padding: "12px 0 12px 18px",
                                            }}
                                          >
                                            <span style={KPI_LABEL}>
                                              {versal(
                                                t(
                                                  "pages.statusReports.report.totalBudget",
                                                ),
                                              )}
                                            </span>
                                            <br />
                                            <span style={KPI_VALUE}>
                                              {fmtEur(totalBudget)}
                                            </span>
                                          </td>
                                          <td
                                            valign="top"
                                            width={165}
                                            {...bg(BRAND.wash)}
                                            style={{
                                              width: 165,
                                              backgroundColor: BRAND.wash,
                                              borderTop: `2px solid ${BRAND.border}`,
                                              borderLeft: `1px solid ${BRAND.border}`,
                                              padding: "12px 0 12px 18px",
                                            }}
                                          >
                                            <span style={KPI_LABEL}>
                                              {versal(
                                                t(
                                                  "pages.statusReports.report.totalConsumed",
                                                ),
                                              )}
                                            </span>
                                            <br />
                                            <span style={KPI_VALUE}>
                                              {fmtEur(totalBilled)}
                                            </span>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  )}

                                  {/*
                                    Stunden und Stichtag als Fusszeile des
                                    Kastens, abgesetzt durch eine Haarlinie.

                                    Beide sind Randangaben zu den Zahlen
                                    darueber. Ohne die Linie stossen sie
                                    unmittelbar an den Restbetrag und lasen
                                    sich wie ein Teil von ihm.
                                  */}
                                  {report.hours != null && (
                                    <p
                                      style={{
                                        margin: "0",
                                        paddingTop: 13,
                                        borderTop: `1px solid ${BRAND.hairline}`,
                                        fontFamily: FONT,
                                        fontSize: 12,
                                        color: BRAND.muted,
                                      }}
                                    >
                                      {t("pages.statusReports.report.bookedHours")}{" "}
                                      {month}:{" "}
                                      {/* `toFixed` kennt kein Zahlenformat und
                                          schrieb „25.75 h" mit englischem
                                          Dezimalpunkt in den Kundenbericht,
                                          während der Mail-Entwurf daneben
                                          „25,75 h" sagte (Befund B4). */}
                                      <strong style={{ color: BRAND.body }}>
                                        {formatHours(report.hours, REPORT_LOCALE)}
                                      </strong>
                                    </p>
                                  )}

                                  {/* Stichtag der Budgetzahlen (Befund B3).
                                      Sie tragen den Stand des Spiegels, nicht
                                      den des Berichtsmonats — eine Historie
                                      gibt Salesforce nicht her (TK §12.5).
                                      Also steht es da, statt dass der Bericht
                                      etwas behauptet, was nicht stimmt. */}
                                  {report.budgetAsOf && (
                                    <p
                                      style={{
                                        margin: "3px 0 0",
                                        fontFamily: FONT,
                                        fontSize: 11,
                                        color: BRAND.faint,
                                      }}
                                      data-testid="budget-as-of"
                                    >
                                      {t("pages.statusReports.report.budgetAsOf")}
                                      : {fmtDate(report.budgetAsOf)}
                                    </p>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      )}

                      {/* ── Offene Tickets ──────────────────────────────── */}
                      <Section
                        title={`${t("pages.statusReports.report.openSection")} (${openTickets.length})`}
                      />
                      <table
                        width="100%"
                        cellPadding={0}
                        cellSpacing={0}
                        border={0}
                        style={{ marginBottom: 18 }}
                      >
                        <tbody>
                          <TicketHeaderRow
                            ticketLabel={t("pages.statusReports.report.colTicket")}
                            summaryLabel={t("pages.statusReports.report.colSummary")}
                            lastLabel={t("pages.statusReports.report.colStatus")}
                            nextStepsLabel={
                              offeneMitSchritten
                                ? t("pages.statusReports.report.colNextSteps")
                                : undefined
                            }
                            accent={BRAND.orange}
                          />
                          {openTickets.map((ticket, i) => {
                            const col = statusColor(ticket.ticketStatus);
                            return (
                              <tr
                                key={ticket.overrideId ?? `${i}-${ticket.summary}`}
                                {...bg(i % 2 === 0 ? "#FFFFFF" : BRAND.wash)}
                              >
                                <td
                                  {...bg(col)}
                                  width={3}
                                  style={{ fontSize: 1, lineHeight: "1px" }}
                                >
                                  &nbsp;
                                </td>
                                <td
                                  width={92}
                                  style={{
                                    padding: openRow.padding,
                                    borderBottom: `1px solid ${BRAND.hairline}`,
                                    whiteSpace: "nowrap",
                                    verticalAlign: "top",
                                  }}
                                >
                                  <TicketKey
                                    jiraKey={ticket.jiraKey}
                                    baseUrl={report.jiraTicketBaseUrl}
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: openRow.padding,
                                    borderBottom: `1px solid ${BRAND.hairline}`,
                                    fontFamily: FONT,
                                    fontSize: openRow.fontSize,
                                    color: BRAND.body,
                                    verticalAlign: "top",
                                  }}
                                >
                                  {ticket.summary}
                                  {ticket.aiSummary && (
                                    /* KI-Zusammenfassung: kursiv, mit farbigem
                                       Balken links — wie im Original. */
                                    <table
                                      cellPadding={0}
                                      cellSpacing={0}
                                      border={0}
                                      style={{ marginTop: 6 }}
                                    >
                                      <tbody>
                                        <tr>
                                          <td
                                            style={{
                                              borderLeft: `2px solid ${col}`,
                                              padding: "1px 0 1px 8px",
                                              fontFamily: FONT,
                                              fontSize: 11,
                                              color: "#9a9a9a",
                                              fontStyle: "italic",
                                              lineHeight: 1.5,
                                            }}
                                          >
                                            {ticket.aiSummary}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: openRow.padding,
                                    borderBottom: `1px solid ${BRAND.hairline}`,
                                    whiteSpace: "nowrap",
                                    verticalAlign: "top",
                                    textAlign: "right",
                                  }}
                                >
                                  <table
                                    cellPadding={0}
                                    cellSpacing={0}
                                    border={0}
                                    align="right"
                                  >
                                    <tbody>
                                      <tr>
                                        <StatusPill status={ticket.ticketStatus} />
                                      </tr>
                                    </tbody>
                                  </table>
                                </td>
                                {offeneMitSchritten && (
                                  <NextStepsCell
                                    text={ticket.nextSteps}
                                    padding={openRow.padding}
                                  />
                                )}
                              </tr>
                            );
                          })}
                          {openTickets.length === 0 && (
                            <tr>
                              <td {...bg(BRAND.orange)} width={3} style={{ fontSize: 1 }}>
                                &nbsp;
                              </td>
                              <td
                                colSpan={3}
                                style={{
                                  padding: "9px 10px",
                                  fontFamily: FONT,
                                  fontSize: 12,
                                  color: BRAND.faint,
                                  fontStyle: "italic",
                                }}
                              >
                                {t("pages.statusReports.report.noOpenTickets")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/*
                        Der Abschnitt erscheint nur, wenn es etwas zu zeigen
                        gibt.

                        Vorher stand er auch dann da, wenn im Monat nichts
                        erledigt wurde — mit „(0)" in der Ueberschrift und der
                        Zeile „keine erledigten Vorgaenge" darunter. Beim
                        Kunden liest sich das wie eine Bilanz des Nichtstuns,
                        obwohl es nur bedeutet, dass in diesem Monat nichts
                        abgeschlossen wurde. Ein leerer Abschnitt sagt weniger
                        als gar keiner.
                      */}
                      {(doneTickets.length > 0 ||
                        report.highlights.length > 0) && (
                        <>
                      {/* ── Erledigte Tickets: Gold-Kasten + Restliste ──── */}
                      <Section
                        title={`${t("pages.statusReports.report.doneSection")} – ${month} (${doneTickets.length})`}
                      />
                      {(highlightItems.length > 0 ||
                        report.highlights.length > 0) && (
                        <table
                          width="100%"
                          cellPadding={0}
                          cellSpacing={0}
                          border={0}
                          style={{
                            marginBottom: 12,
                            border: `1px solid ${BRAND.gold}`,
                            background: "#FFF7DC",
                          }}
                        >
                          <tbody>
                            <tr {...bg("#FFF7DC")}>
                              <td
                                style={{
                                  padding: "7px 14px",
                                  borderBottom: `1px solid ${BRAND.gold}`,
                                  fontFamily: FONT,
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  color: "#D9A500",
                                  textTransform: "uppercase",
                                  letterSpacing: "1px",
                                }}
                              >
                                {highlightItems.length + report.highlights.length > 1
                                  ? t("pages.statusReports.report.highlightsOfMonth")
                                  : t("pages.statusReports.report.highlightOfMonth")}
                              </td>
                            </tr>
                            {/* Aus erledigten Tickets markiert — wie im Original */}
                            {highlightItems.map((ticket, i) => (
                              <tr key={ticket.overrideId ?? `hl-${i}`}>
                                <td
                                  style={{
                                    padding: "7px 14px",
                                    borderBottom: `1px solid ${BRAND.gold}`,
                                    fontFamily: FONT,
                                  }}
                                >
                                  <TicketKey
                                    jiraKey={ticket.jiraKey}
                                    baseUrl={report.jiraTicketBaseUrl}
                                    color="#D9A500"
                                  />{" "}
                                  <span
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: BRAND.body,
                                    }}
                                  >
                                    {ticket.summary}
                                  </span>
                                  {ticket.valueDescription && (
                                    <>
                                      <br />
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: BRAND.muted,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {ticket.valueDescription}
                                      </span>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {/* Freitext-Hervorhebungen ohne Ticketbezug */}
                            {report.highlights.map((h) => (
                              <tr key={h.id}>
                                <td
                                  style={{
                                    padding: "7px 14px",
                                    borderBottom: `1px solid ${BRAND.gold}`,
                                    fontFamily: FONT,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: BRAND.body,
                                  }}
                                >
                                  {h.body}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <table
                        width="100%"
                        cellPadding={0}
                        cellSpacing={0}
                        border={0}
                        style={{ marginBottom: 18 }}
                      >
                        <tbody>
                          {/* Kopfzeile erst ab der kompakten Laenge: Bei drei
                              Zeilen (dem Median) waere sie Ballast, bei
                              sechzig ist sie die Orientierung. */}
                          {doneCompact && (
                            <TicketHeaderRow
                              ticketLabel={t("pages.statusReports.report.colTicket")}
                              summaryLabel={t("pages.statusReports.report.colSummary")}
                              lastLabel={t("pages.statusReports.report.colResolvedOn")}
                              accent={BRAND.green}
                            />
                          )}
                          {regularDone.map((ticket, i) => (
                            <tr
                              key={ticket.overrideId ?? `${i}-${ticket.summary}`}
                              {...bg(i % 2 === 0 ? "#FFFFFF" : BRAND.wash)}
                            >
                              <td
                                {...bg(BRAND.green)}
                                width={3}
                                style={{ fontSize: 1 }}
                              >
                                &nbsp;
                              </td>
                              <td
                                width={92}
                                style={{
                                  padding: doneRow.padding,
                                  borderBottom: `1px solid ${BRAND.hairline}`,
                                  whiteSpace: "nowrap",
                                  verticalAlign: "top",
                                }}
                              >
                                <TicketKey
                                  jiraKey={ticket.jiraKey}
                                  baseUrl={report.jiraTicketBaseUrl}
                                />
                              </td>
                              <td
                                style={{
                                  padding: doneRow.padding,
                                  borderBottom: `1px solid ${BRAND.hairline}`,
                                  fontFamily: FONT,
                                  fontSize: doneRow.fontSize,
                                  color: BRAND.body,
                                  verticalAlign: "top",
                                }}
                              >
                                {ticket.summary}
                              </td>
                              {/* Das Loesungsdatum ist der Anker, an dem sich
                                  eine lange Liste ueberfliegen laesst. */}
                              <td
                                width={90}
                                style={{
                                  padding: doneRow.padding,
                                  borderBottom: `1px solid ${BRAND.hairline}`,
                                  fontFamily: FONT,
                                  fontSize: 11,
                                  color: BRAND.muted,
                                  whiteSpace: "nowrap",
                                  verticalAlign: "top",
                                  textAlign: "right",
                                }}
                              >
                                {ticket.resolvedOn
                                  ? fmtDate(ticket.resolvedOn)
                                  : ""}
                              </td>
                            </tr>
                          ))}
                          {regularDone.length === 0 && (
                            <tr>
                              <td {...bg(BRAND.green)} width={3} style={{ fontSize: 1 }}>
                                &nbsp;
                              </td>
                              <td
                                colSpan={3}
                                style={{
                                  padding: "9px 10px",
                                  fontFamily: FONT,
                                  fontSize: 12,
                                  color: BRAND.faint,
                                  fontStyle: "italic",
                                }}
                              >
                                {t("pages.statusReports.report.noDoneTickets")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      {/* Sagt bei einer langen Liste, wonach sortiert ist —
                          ohne das liest sie sich wie ein ungeordneter Haufen. */}
                      {doneCompact && (
                        <p
                          style={{
                            margin: "-12px 0 18px",
                            fontFamily: FONT,
                            fontSize: 11,
                            color: BRAND.muted,
                          }}
                        >
                          {t("pages.statusReports.report.doneListSortHint", {
                            count: regularDone.length,
                          })}
                        </p>
)}
                        </>
                      )}

                      {/* ── Ticketstatistik (nur mit gesetztem Haken) ───── */}
                      {report.ticketStats !== null &&
                        report.ticketStats.length > 0 && (
                          <>
                            <Section
                              title={t(
                                "pages.statusReports.report.ticketStatsSection",
                              )}
                            />
                            <TicketStatsTable rows={report.ticketStats} />
                          </>
                        )}

                      {/* ── Ansprechpartner & Abwesenheiten ─────────────── */}
                      {report.contacts.length > 0 && (
                        <>
                          <Section
                            title={t("pages.statusReports.report.contactsSection")}
                          />
                          <table
                            width="100%"
                            cellPadding={0}
                            cellSpacing={0}
                            border={0}
                            style={{ marginTop: 6, marginBottom: 18 }}
                          >
                            <tbody>
                              {report.contacts.map((c) => (
                                <tr key={c.employeeId}>
                                  <td
                                    width={40}
                                    valign="top"
                                    style={{
                                      padding: "7px 0 8px",
                                      borderBottom: `1px solid ${BRAND.hairline}`,
                                    }}
                                  >
                                    <table
                                      cellPadding={0}
                                      cellSpacing={0}
                                      border={0}
                                      width={30}
                                      style={{ width: 30 }}
                                    >
                                      <tbody>
                                        <tr>
                                          <td
                                            width={30}
                                            height={30}
                                            align="center"
                                            valign="middle"
                                            /* KEIN `line-height` hier.
                                               Word rechnet eine Zeilenhöhe, die
                                               ein Vielfaches der Schriftgröße
                                               ist, auf die Zeile drauf statt
                                               sie darin zu zentrieren: Bei
                                               11px Schrift in einer 30px hohen
                                               Zelle rutschte „HN" unter die
                                               getönte Fläche und stand daneben
                                               statt darin. `valign="middle"`
                                               zentriert zuverlässig, die
                                               Zeilenhöhe nicht.

                                               Farbfläche statt Kreis: Die
                                               Word-Engine wirft `border-radius`
                                               weg, behält aber den Rahmen — aus
                                               dem Kreis wurde ein umrandetes
                                               Quadrat.

                                               Getönt statt im Vollton, und
                                               zwar aus demselben Grund wie bei
                                               den Status-Badges: Ein oranger
                                               Klotz wäre in einem sonst hellen
                                               Bericht das lauteste Element der
                                               Seite — für die Initialen, also
                                               die beiläufigste Angabe darin. */
                                            {...bg(BRAND.tint)}
                                            style={{
                                              backgroundColor: BRAND.tint,
                                              width: 30,
                                              fontFamily: FONT,
                                              fontSize: 11,
                                              fontWeight: "bold",
                                              textAlign: "center",
                                              letterSpacing: ".5px",
                                              color: BRAND.orange,
                                            }}
                                          >
                                            {c.initials}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </td>
                                  <td
                                    valign="top"
                                    style={{
                                      padding: "7px 0 8px",
                                      borderBottom: `1px solid ${BRAND.hairline}`,
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 3px",
                                        fontFamily: FONT,
                                        fontSize: 13,
                                        fontWeight: "bold",
                                        color: BRAND.grey,
                                      }}
                                    >
                                      {c.fullName}
                                    </p>
                                    {c.absences.length === 0 ? (
                                      <span
                                        style={{
                                          fontFamily: FONT,
                                          fontSize: 12,
                                          color: BRAND.faint,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {t("pages.statusReports.report.noAbsences")}
                                      </span>
                                    ) : (
                                      c.absences.map((a) => (
                                        <span
                                          key={`${a.dateFrom}-${a.dateTo}`}
                                          style={{
                                            fontFamily: FONT,
                                            fontSize: 12,
                                            color: BRAND.body,
                                          }}
                                        >
                                          {/*
                                            Ein Schriftzeichen, kein Kaestchen.

                                            Vorher stand hier ein 6x6-`span`
                                            mit `display:inline-block` und
                                            CSS-`background`. Die Word-Engine
                                            kennt beides nicht: Sie gibt dem
                                            leeren `span` keine Groesse und
                                            malt keinen Grund — der Punkt
                                            verschwand in Outlook spurlos.
                                            Nachgewiesen, indem dem Bericht
                                            alles genommen wurde, was Word
                                            ignoriert; uebrig blieb eine
                                            Leerstelle. Ein Zeichen ueberlebt
                                            jeden Filter, und seine Farbe
                                            traegt ohnehin `<font color>`.
                                          */}
                                          <span
                                            style={{
                                              fontFamily: FONT,
                                              fontSize: 10,
                                              color: BRAND.orange,
                                            }}
                                          >
                                            {"▪"}
                                          </span>
                                          {"  "}
                                          {a.dateFrom === a.dateTo
                                            ? fmtDate(a.dateFrom)
                                            : `${fmtDate(a.dateFrom)} – ${fmtDate(a.dateTo)}`}
                                          <br />
                                        </span>
                                      ))
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {/* ── Events & Webinare ──────────────────────────── */}
                      {report.events.length > 0 && (
                        <>
                          <Section
                            title={t("pages.statusReports.report.eventsSection")}
                          />
                          <table
                            width="100%"
                            cellPadding={0}
                            cellSpacing={0}
                            border={0}
                            style={{ marginBottom: 18 }}
                          >
                            <tbody>
                              {report.events.map((e) => {
                                const [, mon, day] = e.date.split("-");
                                const monShort =
                                  MONTHS_SHORT[Number(mon) - 1] ?? "";
                                // Das `href` stammt aus fremdem HTML (W9) —
                                // nur http/https überleben, alles andere wird
                                // zu reinem Text.
                                const href = safeHttpUrl(e.url);
                                return (
                                  <tr key={`${e.date}-${e.title}`}>
                                    <td
                                      width={68}
                                      valign="top"
                                      style={{ width: 68, padding: "14px 14px 14px 0" }}
                                    >
{/*
                                        Datumskachel — Monat auf getöntem
                                        Grund, Tag groß darunter.

                                        Die Breite steht an JEDER ZELLE, nicht
                                        nur an der Tabelle. Word leitet aus
                                        einer Tabellenbreite keine Zellbreite
                                        ab: Es schrumpft die Zellen auf ihren
                                        Inhalt, und dann steht die Zahl trotz
                                        `align="center"` nicht mittig, sondern
                                        klebt links. Genau deshalb sahen die
                                        Zahlen im Kasten schief aus
                                        (Rückmeldung 04.09.).

                                        Dieselbe Regel gilt überall im
                                        Bericht: Wer eine Zelle zentriert,
                                        gibt ihr auch eine Breite — sonst gibt
                                        es nichts, worin sie zentrieren
                                        könnte.
                                      */}
                                      <table
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        width={54}
                                        style={{
                                          width: 54,
                                          border: `1px solid ${BRAND.tintLine}`,
                                        }}
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              width={54}
                                              height={20}
                                              align="center"
                                              valign="middle"
                                              {...bg(BRAND.tint)}
                                              style={{
                                                width: 54,
                                                backgroundColor: BRAND.tint,
                                                fontFamily: FONT,
                                                fontSize: 9,
                                                fontWeight: "bold",
                                                textTransform: "uppercase",
                                                letterSpacing: ".8px",
                                                textAlign: "center",
                                                lineHeight: "20px",
                                                color: BRAND.orange,
                                              }}
                                            >
                                              {monShort}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td
                                              width={54}
                                              height={32}
                                              align="center"
                                              valign="middle"
                                              {...bg("#FFFFFF")}
                                              style={{
                                                width: 54,
                                                backgroundColor: "#FFFFFF",
                                                fontFamily: FONT,
                                                fontSize: 21,
                                                fontWeight: "bold",
                                                textAlign: "center",
                                                lineHeight: "32px",
                                                color: BRAND.ink,
                                              }}
                                            >
                                              {day}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                    <td
                                      valign="top"
                                      style={{
                                        padding: "12px 0",
                                        borderBottom: `1px solid ${BRAND.hairline}`,
                                      }}
                                    >
                                      {e.eventType && (
                                        <p
                                          style={{
                                            margin: "0 0 2px",
                                            fontFamily: FONT,
                                            fontSize: 10,
                                            fontWeight: "bold",
                                            color: BRAND.orange,
                                            textTransform: "uppercase",
                                            letterSpacing: ".5px",
                                          }}
                                        >
                                          {versal(e.eventType)}
                                        </p>
                                      )}
                                      <p style={{ margin: "0 0 4px" }}>
                                        {href ? (
                                          <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              fontFamily: FONT,
                                              fontSize: 13,
                                              fontWeight: "bold",
                                              color: BRAND.grey,
                                              textDecoration: "none",
                                            }}
                                          >
                                            {e.title}
                                          </a>
                                        ) : (
                                          <span
                                            style={{
                                              fontFamily: FONT,
                                              fontSize: 13,
                                              fontWeight: "bold",
                                              color: BRAND.grey,
                                            }}
                                          >
                                            {e.title}
                                          </span>
                                        )}
                                      </p>
                                      {e.description && (
                                        <p
                                          style={{
                                            margin: 0,
                                            fontFamily: FONT,
                                            fontSize: 12,
                                            color: BRAND.muted,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {e.description}
                                        </p>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr>
                                <td
                                  colSpan={2}
                                  style={{
                                    paddingTop: 4,
                                    fontFamily: FONT,
                                    fontSize: 11,
                                    color: BRAND.muted,
                                  }}
                                >
                                  {t("pages.statusReports.report.allEvents")}:{" "}
                                  <a
                                    href="https://activate-hr.de/events/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: BRAND.orange }}
                                  >
                                    activate-hr.de/events
                                  </a>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      )}

                      {/* ── Grußformel + Signatur ───────────────────────── */}
                      <table
                        width="100%"
                        cellPadding={0}
                        cellSpacing={0}
                        border={0}
                        style={{ marginTop: 6 }}
                      >
                        <tbody>
                          <tr>
                            <td
                              style={{
                                borderTop: "1px solid #E6E6E6",
                                fontSize: 1,
                                lineHeight: "1px",
                              }}
                            >
                              &nbsp;
                            </td>
                          </tr>
                          <tr>
                            <td height={12} style={{ fontSize: 1 }}>
                              &nbsp;
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                fontFamily: FONT,
                                fontSize: 14,
                                color: BRAND.body,
                                lineHeight: 1.6,
                              }}
                            >
                              {t("pages.statusReports.closing")},
                              <br />
                              <strong style={{ fontSize: 15, color: BRAND.grey }}>
                                {signers}
                              </strong>
                              <br />
                              <span style={{ fontSize: 12, color: BRAND.faint }}>
                                mindsquare AG - Service
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>

              <table width={760} cellPadding={0} cellSpacing={0} border={0}>
                <tbody>
                  <tr>
                    <td
                      {...bg(BRAND.border)}
                      height={1}
                      style={{ fontSize: 1, lineHeight: "1px" }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style={{ padding: "12px 0" }}>
                      <span
                        style={{ fontFamily: FONT, fontSize: 10, color: "#BBB" }}
                      >
                        mindsquare AG - Service
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
